import dataHubManager, { DataHubOperationType, DataHubStorageOperations } from '../../managers/data/DataHubManager'
import dataStorageManager, { BucketDefaultIDs } from '../../managers/data/DataStorageManager'
import { IArmador, OperatedArmador } from '../api-services/ArmadoresServices'
import { IChange, RequestedChange } from '../api-services/ChangesServices'
import { IEmbarcacion, OperatedEmbarcacion } from '../api-services/EmbarcacionesServices'
import { IRequest, RequestObjectType, RequestStatus } from '../api-services/RequestsServices'
import { EstadoAO, TipoSuspensionAO } from '../api-services/AutorizacionOperacionServices'
import { parseSafeDate } from '../apiUtils'
import { changesHubName } from './ChangesHub'
import dataHubServices, { defaultRefreshTime, StorageService } from './DataHubServices'

export enum EmbarcacionesHubPipelines {
  EMBARCACIONES_WITH_REQUESTS = 'embarcaciones_with_requests', // Pipeline para mostrar las embarcaciones con su solicitud más reciente (si existe)
  EMBARCACIONES_WITH_CHANGES = 'embarcaciones_with_changes', // Pipeline para mostrar las embarcaciones con sus cambios relacionados
  EMBARCACIONES_WITH_STATUS = 'embarcaciones_with_status', // Pipeline para mostrar las embarcaciones con su estado actual basado en su solicitud más reciente y sus cambios relacionados
  EMBARCACIONES_WITH_ASOCIATIONS = 'embarcaciones_with_asociations', // Pipeline para mostrar las embarcaciones con sus solicitudes de asociación más recientes (si existen)
  EMBARCACIONES_WITH_COBROS = 'embarcaciones_with_cobros', // Pipeline para mostrar las embarcaciones con sus cobros relacionados
  EMBARCACIONES_WITH_CURRENT_COBROS = 'embarcaciones_with_current_cobros', // Pipeline para mostrar las embarcaciones con sus cobros actuales relacionados (cobros que no están pagados ni vencidos)
  EMBARCACIONES_WITH_SALIDAS = 'embarcaciones_with_salidas', // Pipeline para mostrar las embarcaciones con sus salidas relacionadas
  EMBARCACIONES_WITH_SERVICIOS = 'embarcaciones_with_servicios', // Pipeline para mostrar las embarcaciones con sus servicios relacionados
  EMBARCACIONES_FULL = 'embarcaciones_full', // Pipeline completo para mostrar toda la información relacionada a una embarcación (solicitud, cambios, asociación, cobros, salidas, servicios, etc)
}

export const embarcacionesHubName = 'embarcacionesHub'

export class EmbarcacionesHub implements StorageService {
  bucketData: any[] = []

  storageOperations: DataHubStorageOperations<IEmbarcacion> = {
    getData: () => [],
    markDirty: () => {},
    markStaled: () => {},
    forceRefresh: async () => {},
  }
  pipelinesOperations: Map<EmbarcacionesHubPipelines, DataHubStorageOperations<any>> = new Map()

  constructor() {}

  public async startup() {
    dataStorageManager.subscribeBucket(BucketDefaultIDs.EMBARCACIONES, (data) => {
      this.bucketData = data
      this.storageOperations.forceRefresh()
    })
    this.storageOperations = dataHubManager.registerStorage(
      embarcacionesHubName,
      async () => {
        return this.bucketData
      },
      {
        invalidationStrategy: 'hybrid',
        checkTime: defaultRefreshTime,
        lazyLoad: true,
        pipelines: [
          {
            name: EmbarcacionesHubPipelines.EMBARCACIONES_WITH_REQUESTS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: IEmbarcacion[]) => {
                  const dataWithRequests = this.addRequestToEmbarcaciones(data)
                  return dataWithRequests
                },
              },
            ],
          },
          {
            name: EmbarcacionesHubPipelines.EMBARCACIONES_WITH_CHANGES,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addChanges',
                execute: (data: OperatedEmbarcacion[]) => {
                  const dataWithChanges = this.addChangesToEmbarcaciones(data)
                  return dataWithChanges
                },
              },
            ],
          },
          {
            name: EmbarcacionesHubPipelines.EMBARCACIONES_WITH_STATUS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addStatus',
                execute: (data: OperatedEmbarcacion[]) => {
                  const dataWithStatus = this.addStatusToEmbarcaciones(data)
                  return dataWithStatus
                },
              },
            ],
          },
          {
            name: EmbarcacionesHubPipelines.EMBARCACIONES_WITH_ASOCIATIONS,
            operations: [
              {
                type: DataHubOperationType.MAP,
                name: 'addRequests',
                execute: (data: IEmbarcacion[]) => {
                  const dataWithRequests = this.addRequestToEmbarcaciones(data)
                  return dataWithRequests
                },
              },
              {
                type: DataHubOperationType.MAP,
                name: 'addChanges',
                execute: (data: OperatedEmbarcacion[]) => {
                  const dataWithChanges = this.addChangesToEmbarcaciones(data)
                  return dataWithChanges
                },
              },
              {
                type: DataHubOperationType.MAP,
                name: 'addAsociations',
                execute: (data: OperatedEmbarcacion[]) => {
                  const dataWithAsociations = this.addAsociationsToEmbarcaciones(data)
                  return dataWithAsociations
                },
              },
              {
                type: DataHubOperationType.MAP,
                name: 'addStatus',
                execute: (data: OperatedEmbarcacion[]) => {
                  const dataWithStatus = this.addStatusToEmbarcaciones(data)
                  return dataWithStatus
                },
              },
            ],
          },
        ],
      },
    )

    dataStorageManager.subscribeBucket(BucketDefaultIDs.SOLICITUDES, () => {
      console.log('Solicitudes updated, refreshing embarcaciones data...')
      this.storageOperations.forceRefresh()
    })

    dataHubServices.subscribeToHub(changesHubName, {
      subscriberID: 'embarcacionesHub_changes_subscription',
      pipeline: null,
      callback: () => {
        console.log('Changes updated, refreshing embarcaciones data...')
        this.storageOperations.forceRefresh()
      },
    })
  }

  private addRequestToEmbarcaciones(embarcaciones: IEmbarcacion[]): Array<OperatedEmbarcacion> {
    const requestsList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const operatedEmbarcacion: Array<OperatedEmbarcacion> = embarcaciones.map((embarcacion) => {
      const requestData = requestsList
        .filter(
          (req) =>
            req.tipo_objeto === RequestObjectType.Embarcacion || req.tipo_objeto === RequestObjectType.Modificacion,
        )
        .filter((req) => req.id_objeto.toString() === embarcacion.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]

      const aoRequestData = requestsList
        .filter((req) => req.tipo_objeto === RequestObjectType.AutorizacionOperacion)
        .filter((req) => req.id_objeto.toString() === embarcacion.id.toString())
        .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0]

      let aoData: any = undefined
      if (aoRequestData && aoRequestData.estado === RequestStatus.APPROVED) {
        const esSuspendidaAdmin = !!(
          aoRequestData.razon_rechazo && aoRequestData.razon_rechazo.startsWith('SUSPENDIDA:')
        )
        const parsedCaducidad = parseSafeDate(aoRequestData.fecha_caducidad)
        const caducada = parsedCaducidad ? parsedCaducidad.getTime() < Date.now() : false
        const parsedCN = parseSafeDate(embarcacion.fecha_vencimiento)
        const cnVencido = parsedCN ? parsedCN.getTime() <= Date.now() : false

        // PP-AO-09: La vigencia sigue corriendo durante suspensión. Caducada prevalece sobre suspendida.
        const estadoAO = caducada
          ? EstadoAO.CADUCADA
          : esSuspendidaAdmin
            ? EstadoAO.SUSPENDIDA
            : cnVencido
              ? EstadoAO.SUSPENDIDA
              : EstadoAO.VIGENTE

        const tipoSuspension = esSuspendidaAdmin
          ? TipoSuspensionAO.ADMINISTRATIVA
          : cnVencido
            ? TipoSuspensionAO.CN_VENCIDO
            : TipoSuspensionAO.NINGUNA

        const motivoSuspension = esSuspendidaAdmin
          ? aoRequestData.razon_rechazo?.replace('SUSPENDIDA:', '').trim()
          : null

        aoData = {
          id: embarcacion.id,
          numero_ao: `AO-${embarcacion.id.toString().padStart(4, '0')}`,
          solicitud_id: aoRequestData.id,
          embarcacion_id: embarcacion.id,
          armador_id: aoRequestData.id_objeto_2 ? Number(aoRequestData.id_objeto_2) : 0,
          tipo_autorizacion: 0,
          estado: estadoAO,
          tipo_suspension: tipoSuspension,
          motivo_suspension: motivoSuspension,
          fecha_inicio: aoRequestData.fecha_solicitud,
          fecha_vencimiento: aoRequestData.fecha_caducidad,
          fecha_vencimiento_cn: embarcacion.fecha_vencimiento,
          posee_convenio: embarcacion.convenio === 1,
        }
      }

      return {
        ...embarcacion,
        requestData: requestData,
        aoRequestData: aoRequestData,
        aoData: aoData,
        changesData: [],
        asociationsData: [],
        cobrosData: [],
        currentCobrosData: [],
        salidasData: [],
        serviciosData: [],
      }
    })
    return operatedEmbarcacion
  }

  private addChangesToEmbarcaciones(embarcaciones: OperatedEmbarcacion[]): Array<OperatedEmbarcacion> {
    const fullChangesHub = dataHubServices.getHub(changesHubName)
    const fullChangesList: Array<RequestedChange> = fullChangesHub ? fullChangesHub.storageOperations.getData() : []
    const operatedEmbarcaciones: Array<OperatedEmbarcacion> = embarcaciones.map((item) => {
      const changesData = fullChangesList.filter((ch) => ch.id_objeto.toString() === item.id.toString())
      return {
        ...item,
        changesData: changesData,
      }
    })
    return operatedEmbarcaciones
  }

  private addStatusToEmbarcaciones(embarcaciones: OperatedEmbarcacion[]): Array<OperatedEmbarcacion> {
    const operatedEmbarcaciones: Array<OperatedEmbarcacion> = embarcaciones.map((item) => {
      let status = item.requestData ? item.requestData.estado : RequestStatus.INCOMPLETE
      const matriculaValid = item.id_matricula !== 0
      const fotografiaValid = item.id_fotografía !== 0
      const certificadoValid = item.id_certifricado_navegacion !== 0

      const validDocs = fotografiaValid && matriculaValid && certificadoValid
      // Las auxiliares no exigen documentos: se consideran completas siempre.
      const embarcacionCompleta = item.tipo_embarcacion == 'auxiliar' || validDocs
      if (!embarcacionCompleta) {
        status = RequestStatus.INCOMPLETE
        item.requestData = undefined
        item.changesData = []
      } else if (!item.requestData) {
        // Solicitud siempre visible: la embarcación está completa pero nunca se le generó la
        // solicitud de inscripción (p. ej. cargada por otra vía). Se sintetiza una solicitud
        // VIRTUAL en estado PENDING para que el administrador la vea y pueda aprobar/rechazar.
        // No existe en el backend: al resolverla se materializa (insert) desde RequestShower.saveChanges.
        item.requestData = this.buildVirtualEmbarcacionRequest(item)
        item.changesData = []
        status = RequestStatus.PENDING
      }
      return {
        ...item,
        status: status,
      }
    })
    return operatedEmbarcaciones
  }

  // Construye una solicitud de embarcación VIRTUAL (no persistida) en estado PENDING. Espeja el
  // patrón de TripulantesHub.buildVirtualTripulanteRequest: id=0, esVirtual=true y el estado se deja
  // SIEMPRE PENDING porque la decisión de aprobar/rechazar es del administrador (y al tomarla se
  // materializa en el backend con op_insert_solicitud).
  private buildVirtualEmbarcacionRequest(item: OperatedEmbarcacion): IRequest {
    return {
      id: 0,
      creador: 0,
      revisor: null,
      estado: RequestStatus.PENDING,
      fecha_solicitud: new Date().toISOString(),
      fecha_respuesta: null,
      fecha_caducidad: new Date(new Date().getFullYear() + 1, 11, 31).toISOString(),
      tipo_objeto: RequestObjectType.Embarcacion,
      id_objeto: item.id.toString(),
      tipo_objeto_2: null,
      id_objeto_2: null,
      razon_rechazo: '',
      esVirtual: true,
    }
  }

  private addAsociationsToEmbarcaciones(embarcaciones: OperatedEmbarcacion[]): Array<OperatedEmbarcacion> {
    const fullRequestList = dataStorageManager.getCurrentBucketData<IRequest>(BucketDefaultIDs.SOLICITUDES) || []
    const asociationsRequests = fullRequestList.filter(
      (req) => req.tipo_objeto === RequestObjectType.Asociacion_embarcacion,
    )
    const operatedEmbarcaciones: Array<OperatedEmbarcacion> = embarcaciones.map((item) => {
      const asociationsData = asociationsRequests.filter((req) => req.id_objeto.toString() === item.id.toString())
      return {
        ...item,
        asociationsData: asociationsData,
      }
    })
    return operatedEmbarcaciones
  }
}

/*

const { showList, reload, isLoading, filters, statusFilter, sorting, pagination, error, performAction } =
  useComplexBucketList<OperatedEmbarcacion, IEmbarcacion>(BucketDefaultIDs.EMBARCACIONES, (item) => {
    let request: IRequest | undefined = requestsList.value
      .filter((req) => req.tipo_objeto === RequestObjectType.Embarcacion)
      .filter((req) => req.id_objeto.toString() === item.id.toString())
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())[0] // Get latest request
    let changesData = fullChangesList.value.filter((ch) => ch.id_objeto.toString() === item.id.toString())
    const asociationsData = asociationsRequests.value
      .filter((req) =>
        req.id_objeto.toString().includes('-')
          ? req.id_objeto.toString().split('-')[0].toString() === item.id.toString()
          : req.id_objeto.toString() === item.id.toString(),
      )
      .sort((a, b) => new Date(b.fecha_solicitud).getTime() - new Date(a.fecha_solicitud).getTime())

    let status = request ? request.estado : RequestStatus.INCOMPLETE
    const matriculaValid = item.id_matricula !== 0
    const fotografiaValid = item.id_fotografía !== 0
    const certificadoValid = item.id_certifricado_navegacion !== 0

    const validDocs = fotografiaValid && matriculaValid && certificadoValid
    if (item.tipo_embarcacion != 'auxiliar' && !validDocs) {
      status = RequestStatus.INCOMPLETE
      request = undefined
      changesData = []
    }
    return {
      ...item,
      requestData: request,
      changesData: changesData,
      asociationsData: asociationsData,
      cobrosData: [],
      currentCobrosData: [],
      salidasData: [],
      serviciosData: [],
    }
  })


*/
